import type { Part } from "@a2a-js/sdk";
import React, { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";

import { useCurrentAccount } from "components/Account/useCurrentAccount";

import { cancelTask, sendMessageStream } from "./api";
import { extractContextId, extractTaskId } from "./contextId";
import {
  logSendMessageCall,
  logStreamError,
  logStreamEvent,
  logStreamFinished,
  logUserMessage,
} from "./copilotLogs";

const delay = (ms: number) =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

const CopilotContext = React.createContext<{
  copilotState: CopilotState;
  openCopilot: () => void;
  closeCopilot: () => void;
  createThread: (documentId: string) => string;
  deleteThreads: (documentId: string) => void;
  sendMessage: (params: {
    documentId: string;
    threadId: string;
    message: string;
  }) => Promise<void>;
  stopThread: (documentId: string, threadId: string) => void;
  updateThread: (
    documentId: string,
    threadId: string,
    partial: Partial<ThreadState>
  ) => void;
}>({
  copilotState: {
    open: false,
    documentThreads: {},
  },
  openCopilot: () => {},
  closeCopilot: () => {},
  createThread: () => uuidv4(),
  deleteThreads: () => {},
  sendMessage: async () => {},
  stopThread: async () => {},
  updateThread: () => {},
});

type ThreadState = {
  name: string;
  messages: {
    id: string;
    content?: string;
    type: "user" | "chatbot";
    parts?: Part[];
  }[];
  contextId?: string; // the backend context ID associated with this thread
  isLoading?: boolean;
  userInput: string;
  error?: string;
  taskId?: string;
};

type CopilotState = {
  open: boolean;
  // documentId -> threadId (client side generated) -> ThreadState
  documentThreads: Record<
    string,
    {
      activeThreadId: string;
      threads: Record<string, ThreadState>;
    } | null
  >;
};

export const useCopilot = () => {
  const context = React.useContext(CopilotContext);
  return context;
};

export const CopilotProvider = (props: React.PropsWithChildren) => {
  const { children } = props;
  const [copilotState, setCopilotState] = useState<CopilotState>({
    open: false,
    documentThreads: {},
  });
  const copilotStateRef = useRef<CopilotState>(copilotState);
  const { getAccount } = useCurrentAccount();
  const { state: documentsState } = useDocuments();

  useEffect(() => {
    copilotStateRef.current = copilotState;
  }, [copilotState]);

  const openCopilot = () => {
    setCopilotState(state => {
      return {
        ...state,
        open: true,
      };
    });
  };

  const closeCopilot = () => {
    setCopilotState(state => ({
      ...state,
      open: false,
    }));
  };

  const createThread = (documentId: string): string => {
    const threadId = uuidv4();

    const threadNames = Object.values(
      copilotState.documentThreads[documentId]?.threads ?? {}
    ).map(thread => thread.name);
    const threadName =
      threadNames.length === 0 ? "Chat 1" : `Chat ${threadNames.length + 1}`;

    setCopilotState(state => {
      const docData = state.documentThreads[documentId] || {
        activeThreadId: null,
        threads: {},
      };
      return {
        ...state,
        documentThreads: {
          ...state.documentThreads,
          [documentId]: {
            activeThreadId: threadId,
            threads: {
              ...docData.threads,
              [threadId]: {
                name: threadName,
                messages: [],
                userInput: "",
              },
            },
          },
        },
      };
    });
    return threadId;
  };

  const addMessage = (
    documentId: string,
    threadId: string,
    type: "user" | "chatbot",
    content: string
  ) => {
    setCopilotState(state => {
      if (!state.documentThreads[documentId]) {
        return state;
      }

      const docData = state.documentThreads[documentId] || {
        activeThreadId: null,
        threads: {},
      };
      const thread = docData.threads[threadId];
      const newMessage = {
        id: uuidv4(),
        content,
        type,
      };

      return {
        ...state,
        documentThreads: {
          ...state.documentThreads,
          [documentId]: {
            activeThreadId: docData.activeThreadId,
            threads: {
              ...docData.threads,
              [threadId]: {
                ...thread,
                messages: [...thread.messages, newMessage],
              },
            },
          },
        },
      };
    });
  };

  const updateThread = (
    documentId: string,
    threadId: string,
    partial: Partial<ThreadState>
  ) => {
    setCopilotState(state => {
      if (!state.documentThreads[documentId]) {
        return state;
      }

      const docData = state.documentThreads[documentId] || {
        activeThreadId: null,
        threads: {},
      };
      const thread = docData.threads[threadId];
      const keys = Object.keys(partial) as (keyof ThreadState)[];
      const isNoopUpdate =
        keys.length > 0 && keys.every(key => thread?.[key] === partial[key]);

      if (isNoopUpdate) {
        return state;
      }
      return {
        ...state,
        documentThreads: {
          ...state.documentThreads,
          [documentId]: {
            activeThreadId: docData.activeThreadId,
            threads: {
              ...docData.threads,
              [threadId]: { ...thread, ...partial },
            },
          },
        },
      };
    });
  };

  const upsertMessage = (
    documentId: string,
    threadId: string,
    id: string,
    data: { content?: string; type: "user" | "chatbot"; parts?: Part[] }
  ) => {
    setCopilotState(state => {
      if (!state.documentThreads[documentId]) {
        return state;
      }

      const thread = state.documentThreads[documentId].threads[threadId];
      if (!thread || !thread.messages) return state;

      const existingMessage = thread.messages.find(
        (message: ThreadState["messages"][number]) => message.id === id
      );

      let messages = [];
      if (existingMessage) {
        const existingParts: Part[] = existingMessage.parts ?? [];
        const incomingParts: Part[] = data.parts ?? [];
        const merged: Part[] = [...existingParts, ...incomingParts];
        // Deduplicate progress/data parts to avoid repeated Reasoning/Notification updates
        const deduped: Part[] = [];
        const seenDataKeys = new Set<string>();
        for (const part of merged) {
          if (part && part.kind === "data") {
            const key = JSON.stringify(part.data ?? null);
            if (seenDataKeys.has(key)) {
              // eslint-disable-next-line no-continue
              continue;
            }
            seenDataKeys.add(key);
            deduped.push(part);
          } else {
            deduped.push(part);
          }
        }
        messages = thread.messages.map(
          (message: ThreadState["messages"][number]) =>
            message.id === id
              ? { ...message, ...data, parts: deduped }
              : message
        );
      } else {
        messages = [...thread.messages, { id, ...data }];
      }

      return {
        ...state,
        documentThreads: {
          ...state.documentThreads,
          [documentId]: {
            activeThreadId: state.documentThreads[documentId].activeThreadId,
            threads: {
              ...state.documentThreads[documentId].threads,
              [threadId]: { ...thread, messages },
            },
          },
        },
      };
    });
  };

  const sendMessage = async ({
    documentId,
    threadId,
    message: userText,
  }: {
    documentId: string;
    threadId: string;
    message: string;
  }) => {
    addMessage(documentId, threadId, "user", userText);
    // Log user message entry
    try {
      logUserMessage({ documentId, threadId, message: userText });
    } catch (_err) {
      // ignore logging errors
    }
    updateThread(documentId, threadId, {
      error: undefined,
      isLoading: true,
      userInput: "",
      // always clear task id and context id on new request
      taskId: undefined,
      contextId: undefined,
    });

    try {
      const account = getAccount();
      const doc = documentsState.documents.find(d => d.id === documentId);
      const rawDbName = doc?.context?.database?.name;
      const catalogName =
        typeof rawDbName === "string" && rawDbName.length > 0
          ? rawDbName
          : undefined;
      const existingContextId =
        copilotState.documentThreads[documentId]?.threads?.[threadId]
          ?.contextId;
      // Log send message call
      try {
        logSendMessageCall({
          documentId,
          threadId,
          message: userText,
          contextId: existingContextId,
          metadata: {
            accountName: account.accountName,
            catalogName,
          },
        });
      } catch (_err) {
        // ignore logging errors
      }

      const stream = sendMessageStream({
        contextId: existingContextId,
        message: userText,
        metadata: {
          accountName: account.accountName,
          catalogName,
        },
      });

      for await (const event of stream) {
        // Prevent the event from being processed in the same tick
        // event proocessing is reading state from the ref
        await delay(1);

        const contextId = extractContextId(event);
        const taskId = extractTaskId(event);

        // Log the raw stream event
        try {
          logStreamEvent({
            documentId,
            threadId,
            event,
            taskId,
            contextId,
          });
        } catch (_err) {
          // ignore logging errors
        }

        const threadCurrentTaskId =
          copilotStateRef.current.documentThreads[documentId]?.threads?.[
            threadId
          ]?.taskId;

        if (
          !threadCurrentTaskId &&
          event.kind === "status-update" &&
          event.status.state === "submitted"
        ) {
          // Extract task id and context id from the initial event
          if (taskId && contextId) {
            updateThread(documentId, threadId, { taskId, contextId });
          }
          // eslint-disable-next-line no-continue
          continue;
        }

        if (threadCurrentTaskId !== taskId) {
          // Skip event. It's a stale event.
          // eslint-disable-next-line no-continue
          continue;
        }

        switch (event.kind) {
          case "status-update": {
            const allParts = event.status.message?.parts ?? [];
            const dataParts = allParts.filter(
              (p: any) => p && p.kind === "data"
            );
            const stableId = event.taskId;
            if (dataParts.length > 0 && stableId) {
              upsertMessage(documentId, threadId, stableId, {
                parts: dataParts,
                type: "chatbot",
              });
            }
            const isFinal = (event as any).final === true;
            const taskState = event.status?.state as string | undefined;
            if (isFinal && taskState === "failed") {
              updateThread(documentId, threadId, {
                error:
                  "Firebolt Agent failed to complete your request. Please try again.",
              });
            }
            break;
          }
          case "artifact-update": {
            const artifactParts = event.artifact.parts ?? [];
            if (artifactParts.length > 0) {
              upsertMessage(documentId, threadId, event.taskId, {
                type: "chatbot",
                parts: artifactParts,
              });
            }
            break;
          }
          default: {
            // ignore other events
          }
        }
      }
    } catch (e: any) {
      try {
        logStreamError({
          documentId,
          threadId,
          errorMessage: e?.message || "Unexpected error",
        });
      } catch (_err) {
        // ignore logging errors
      }
      updateThread(documentId, threadId, {
        error: e?.message || "Unexpected error",
      });
    } finally {
      try {
        logStreamFinished({ documentId, threadId });
      } catch (_err) {
        // ignore logging errors
      }
      updateThread(documentId, threadId, { isLoading: false });
    }
  };

  const stopThread = (documentId: string, threadId: string) => {
    const taskId =
      copilotState.documentThreads[documentId]?.threads?.[threadId]?.taskId;

    if (taskId) {
      cancelTask(taskId);
    }
    updateThread(documentId, threadId, {
      isLoading: false,
      taskId: undefined,
      contextId: undefined,
    });
  };

  const stopAllThreads = (documentIdToStop?: string) => {
    Object.entries(copilotState.documentThreads).forEach(
      ([documentId, documentThread]) => {
        if (documentId && documentId !== documentIdToStop) {
          return;
        }
        if (documentThread) {
          Object.entries(documentThread.threads).forEach(([threadId]) => {
            stopThread(documentId, threadId);
          });
        }
      }
    );
  };

  const deleteThreads = (documentId: string) => {
    stopAllThreads(documentId);

    setCopilotState(state => {
      return {
        ...state,
        documentThreads: {
          ...state.documentThreads,
          [documentId]: null,
        },
      };
    });
  };

  useEffect(() => {
    // If there is a document in copilot state that is not in documentsState, stop all threads of this doc
    const copilotDocumentIds = Object.keys(copilotState.documentThreads);
    const currentDocumentIds = documentsState.documents.map(doc => doc.id);

    copilotDocumentIds.forEach(documentId => {
      const documentExists = currentDocumentIds.includes(documentId);
      if (!documentExists) {
        // Stop all threads for this document
        stopAllThreads(documentId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsState]);

  useEffect(() => {
    return () => {
      stopAllThreads();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const context = {
    copilotState,
    openCopilot,
    closeCopilot,
    createThread,
    deleteThreads,
    sendMessage,
    stopThread,
    updateThread,
  };

  return (
    <CopilotContext.Provider value={context}>
      {children}
    </CopilotContext.Provider>
  );
};
